<template>
  <div class="equipments">
    <OmxList
        :data="filteredEquipmentsData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasSearch ? 'filter-variant' : 'wrench-outline'"
        :empty-title="hasSearch ? $t('omx-list.empty-title') : $t('equipments-view-empty.title')"
        :empty-body="hasSearch ? null : $t('equipments-view-empty.body')"
        :skeleton-columns="[1.6, 1, 1, 0.8, 0.8, 0.8, 1.6]"
    >
      <template #actions>
        <OmxPermissionButton :allowed="canCreate" type="is-primary" icon-left="plus-thick" @click="addNewEquipmentClicked">
          {{ $t('equipments-view.new-equipment-button') }}
        </OmxPermissionButton>
      </template>

      <template #empty-actions>
        <OmxPermissionButton v-if="!hasSearch" :allowed="canCreate" type="is-primary" icon-left="plus-thick" @click="addNewEquipmentClicked">
          {{ $t('equipments-view.new-equipment-button') }}
        </OmxPermissionButton>
        <b-button v-if="hasSearch" @click="search = ''">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="equipment.model" :label="$t('equipments-view.equipment-model-column')" v-slot="props" sortable>
        <router-link
            :to="{ name: 'EquipmentDetails', params: { project_id: project_id, equipment_id: props.row.equipment.document_id, is_template: is_template }}"
            class="omx-row-title"
        >{{ getAdjustedTitle(props.row.equipment.model) }}</router-link>
      </b-table-column>

      <b-table-column field="equipment.category" :label="$t('equipments-view.equipment-category-column')" v-slot="props" sortable>
        {{ props.row.equipment.category }}
      </b-table-column>

      <b-table-column field="equipment.provider" :label="$t('equipments-view.equipment-provider-column')" v-slot="props" sortable>
        {{ props.row.equipment.provider }}
      </b-table-column>

      <b-table-column field="equipment.meta.estimation.quantity" :label="$t('equipments-view.equipment-quantity-column')" v-slot="props" sortable>
        {{ getFormattedQuantityEquipment(props.row.equipment.meta.estimation, props.row.equipment.unit) }}
      </b-table-column>

      <b-table-column field="equipment.meta.estimation.fee_per_unit" :label="$t('equipments-view.equipment-fee-per-unit-column')" v-slot="props" sortable>
        {{ getFormattedFeePerUnit(props.row.equipment.meta.estimation) }}
      </b-table-column>

      <b-table-column field="equipment.meta.estimation.expected_total_fee" :label="$t('equipments-view.equipment-total-fee-column')" v-slot="props" sortable>
        {{ getFormattedPricing(props.row.estimation.expected_total_fee) }}
      </b-table-column>

      <b-table-column field="text" :label="$t('equipments-view.equipment-text-column')" v-slot="props">
        {{ truncateColumn(props.row.equipment.text) }}
      </b-table-column>
    </OmxList>

    <b-modal :width="640" v-model="isModalActive" trap-focus :destroy-on-hide="true" aria-role="dialog" aria-modal>
      <template #default="props">
        <EquipmentDetails :project_id="project_id" equipment_id="new" @close="props.close" @equipmentAdded="readEquipmentsData" />
      </template>
    </b-modal>
  </div>
</template>

<script>
import EquipmentDetails from "@/components/EquipmentDetails";
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Equipments",
  components: { OmxList, EquipmentDetails },
  props: {
    project_id: { type: String },
    is_template: { type: Boolean }
  },
  data() {
    return {
      equipmentsData: [],
      isLoading: false,
      isModalActive: false,
      search: ""
    };
  },
  computed: {
    canCreate() {
      return (!this.is_template && this.currentUserAccess('EquipmentDetails', this.UserActions.CREATE, this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', this.UserActions.CREATE));
    },
    hasSearch() { return this.search.trim() !== ""; },
    filteredEquipmentsData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.equipmentsData;
      return this.equipmentsData.filter(row => {
        const e = row.equipment || {};
        return [e.model, e.category, e.provider, e.text].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
  },
  methods: {
    readEquipmentsData() {
      this.isModalActive = false;
      this.isLoading = true;
      this.equipmentsData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getEquipmentsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getEquipmentsFunction({ action: "get_equipments", project_id: this.project_id, fill_meta: true })
          .then((result) => {
            if (result.data.equipments.length > 0) {
              result.data["equipments"].forEach((equipment) => {
                this.equipmentsData.push({
                  equipment,
                  estimation: equipment.meta.estimation ? equipment.meta.estimation : {}
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message);
            console.log("Error getting equipments:", error?.code);
            this.isLoading = false;
          });
    },
    addNewEquipmentClicked() { this.isModalActive = true; }
  },
  mounted() {
    this.readEquipmentsData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.equipments { margin-top: 16px; }
.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
